import { createApp } from "vue";
import VideoWidget from "./components/VideoWidget.vue";
import VideoAnalytics from "./analytics.js";
import widgetStyles from "./styles/widget.css";

class VideoStoriesWidget {
    constructor(options = {}) {
        this.options = {
            apiKey: options.apiKey || "",
            apiUrl:
            options.apiUrl ||
            "https://api.serviceiro.com.br/embed/videos",
            containerId: options.containerId || null,
            autoplay: options.autoplay !== false,
            muted: options.muted !== false,
            theme: options.theme || "default",
            position: options.position || "bottom-right",
            size: options.size || "medium",
            showControls: options.showControls !== false,
            devMode: options.devMode || process.env.NODE_ENV === "development",
            analytics: options.analytics || null,
            onVideoStart: options.onVideoStart || null,
            onVideoEnd: options.onVideoEnd || null,
            onStoryChange: options.onStoryChange || null,
            onVideoProgress: options.onVideoProgress || null,
            onPlayerOpen: options.onPlayerOpen || null,
            onPlayerClose: options.onPlayerClose || null,
            onVideoInteraction: options.onVideoInteraction || null,
            ...options,
        };

        this.app = null;
        this.stories = [];
        this.isLoaded = false;
        this.container = null;
        this.shadowRoot = null;
        this.widgetInstance = null;
        this.analytics = null;

        // Initialize analysis is configured
        if (this.options.analytics) {
            this.analytics = new VideoAnalytics(
                // Parameter 1: Client GA4 ID (can be undefined)
                this.options.analytics.measurementId,

                // Parameter 2: Options such as 'debug' and 'enabled'
                this.options.analytics.options || {},

                // Parameter 4: The widget's API key to identify the client
                this.options.apiKey
            );
        }

        this.init();
    }

    async init() {
        try {
            if (!this.options.apiKey && !this.options.devMode) {
                throw new Error("API key is required");
            }

            await this.loadStories();
            this.createContainer();
            this.mountWidget();

            this.isLoaded = true;
            this.dispatchEvent("widget-loaded");

            this.isLoaded = true;
            this.dispatchEvent("widget-loaded");

            let lastUrl = location.href;
            new MutationObserver(() => {
                const url = location.href;
                if (url !== lastUrl) {
                    lastUrl = url;
                    this.refresh();
                }
            }).observe(document, { subtree: true, childList: true });
        } catch (error) {
            console.error("VideoStoriesWidget Error:", error);

            if (this.options.devMode || this.options.apiKey === "test-key") {
                console.log("Usando dados mock para desenvolvimento...");
                this.stories = this.getMockStories();
                this.createContainer();
                this.mountWidget();
                this.isLoaded = true;
                this.dispatchEvent("widget-loaded");
            } else {
                this.dispatchEvent("widget-error", { error: error.message });
            }
        }
    }

    async loadStories() {
        if (this.options.devMode || this.options.apiKey === "test-key") {
            this.stories = this.getMockStories();
            return;
        }
        try {
            const currentUrl = window.location.href;

            const response = await fetch(
                `${this.options.apiUrl}?url=${encodeURIComponent(currentUrl)}`,
                {
                    headers: {
                        Authorization: `Bearer ${this.options.apiKey}`,
                        "Content-Type": "application/json",
                    },
                });
            if (!response.ok) {
                throw new Error(
                    `HTTP ${response.status}: ${response.statusText}`,
                );
            }
            const data = await response.json();

            const storiesArray = Array.isArray(data)
                ? data
                : Array.isArray(data.videos)
                ? data.videos
                : [];

            this.stories = this.normalizeStories(storiesArray);
        } catch (error) {
            console.error("Failed to load stories:", error);
            throw error;
        }
    }

    normalizeStories(rawStories) {
        if (!Array.isArray(rawStories)) {
            console.warn("rawStories is not an array:", rawStories);
            return [];
        }

        return rawStories.map(
            (story) => (
                {
                    id: story.id,
                    name: story.title || story.name,
                    description: story.description,
                    videos: (Array.isArray(story.videos)
                        ? story.videos
                        : typeof story.videos === "string"
                        ? JSON.parse(story.videos)
                        : story.videos || []
                    ).map((video) => ({
                        url: video.url || video.src || video.attachment,
                        thumbnail:
                        (video.thumbnail || video.poster || video.thumb) ??
                        "",
                        duration: video.duration || 0,
                        poster: (video.poster || video.thumbnail) ?? "",
                    })),
                    metadata: {
                        created_at: story.created_at,
                        updated_at: story.updated_at,
                        author: story.author,
                        tags: story.tags || [],
                    },
                }
            ),
        );
    }

    getMockStories() {
        return [
            {
                id: 1,
                name: "Big Buck Bunny",
                description: "Desc video mock 1",
                videos: [
                    {
                        url: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4",
                        thumbnail:
                        "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/images/BigBuckBunny.jpg",
                        poster: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/images/BigBuckBunny.jpg",
                    },
                ],
            },
            {
                id: 2,
                name: "Elephants Dream",
                description: "Desc video mock 2",
                videos: [
                    {
                        url: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ElephantsDream.mp4",
                        thumbnail:
                        "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/images/ElephantsDream.jpg",
                        poster: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/images/ElephantsDream.jpg",
                    },
                ],
            },
            {
                id: 3,
                name: "Multiple Videos Story",
                description: "Desc video mock 3",
                videos: [
                    {
                        url: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerJoyrides.mp4",
                        thumbnail:
                        "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/images/ForBiggerJoyrides.jpg",
                        poster: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/images/ForBiggerJoyrides.jpg",
                    },
                    {
                        url: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerMeltdowns.mp4",
                        thumbnail:
                        "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/images/ForBiggerMeltdowns.jpg",
                        poster: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/images/ForBiggerMeltdowns.jpg",
                    },
                ],
            },
        ];
    }

    createContainer() {
        if (this.options.containerId) {
            this.container = document.getElementById(this.options.containerId);
            if (!this.container) {
                throw new Error(
                    `Container with id "${this.options.containerId}" not found`,
                );
            }
        } else {
            this.container = document.createElement("div");
            this.container.className = "video-stories-widget-host";
            this.applyPositioning(this.container);
            document.body.appendChild(this.container);
        }

        this.shadowRoot = this.container.attachShadow({ mode: "open" });

        const appContainer = document.createElement("div");
        appContainer.className = this.getContainerClasses();
        appContainer.setAttribute("role", "application");
        appContainer.setAttribute("aria-label", "Video Stories Widget");

        this.injectStyles();

        this.shadowRoot.appendChild(appContainer);
    }

    injectStyles() {
        const styleEl = document.createElement("style");
        styleEl.textContent = widgetStyles;
        this.shadowRoot.appendChild(styleEl);
    }

    getContainerClasses() {
        return [
            "video-stories-widget",
            `video-stories-widget--${this.options.theme}`,
            `video-stories-widget--${this.options.position}`,
            `video-stories-widget--${this.options.size}`,
            ].join(" ");
    }

    applyPositioning(container) {
        const positions = {
            "bottom-right": { bottom: "20px", right: "20px" },
            "bottom-left": { bottom: "20px", left: "20px" },
            "top-right": { top: "20px", right: "20px" },
            "top-left": { top: "20px", left: "20px" },
            center: {
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
            },
        };

        const pos =
            positions[this.options.position] || positions["bottom-right"];

        Object.assign(container.style, {
            position: "fixed",
            zIndex: "999999",
            pointerEvents: "auto",
            ...pos,
        });
    }

    mountWidget() {
        const appContainer = this.shadowRoot.querySelector(
            ".video-stories-widget",
        );

        this.app = createApp(VideoWidget, {
            stories: this.stories,
            autoplay: this.options.autoplay,
            muted: this.options.muted,
            showControls: this.options.showControls,
            analytics: this.analytics,

            onVideoStart: (story, videoData) => {
                this.dispatchEvent("video-start", { story, videoData });
                if (this.options.onVideoStart) {
                    this.options.onVideoStart(story, videoData);
                }
            },
            onVideoEnd: (videoData) => {
                this.dispatchEvent("video-end", { videoData });
                if (this.options.onVideoEnd) {
                    this.options.onVideoEnd(videoData);
                }
            },
            onStoryChange: (story) => {
                this.dispatchEvent("story-change", { story });
                if (this.options.onStoryChange) {
                    this.options.onStoryChange(story);
                }
            },
            onVideoProgress: (progressData) => {
                this.dispatchEvent("video-progress", { progressData });
                if (this.options.onVideoProgress) {
                    this.options.onVideoProgress(progressData);
                }
            },
            onPlayerOpen: () => {
                this.dispatchEvent("player-open");
                if (this.options.onPlayerOpen) {
                    this.options.onPlayerOpen();
                }
            },
            onPlayerClose: () => {
                this.dispatchEvent("player-close");
                if (this.options.onPlayerClose) {
                    this.options.onPlayerClose();
                }
            },
            onVideoInteraction: (interactionData) => {
                this.dispatchEvent("video-interaction", { interactionData });
                if (this.options.onVideoInteraction) {
                    this.options.onVideoInteraction(interactionData);
                }
            },
        });

        const instance = this.app.mount(appContainer);
        this.widgetInstance = instance;
    }

    dispatchEvent(eventName, detail = {}) {
        const event = new CustomEvent(`videostories:${eventName}`, {
            detail: {
                widget: this,
                timestamp: Date.now(),
                version: this.getVersion(),
                ...detail,
            },
            bubbles: true,
            composed: true,
        });

        if (this.container) {
            this.container.dispatchEvent(event);
        }

        window.dispatchEvent(event);

        if (this.options.devMode) {
            //console.log(`VideoStories Event: ${eventName}`, detail);
        }
    }

    // Public API
    show() {
        if (this.container) {
            this.container.style.display = "block";
            this.dispatchEvent("widget-shown");
        }
        return this;
    }

    hide() {
        if (this.container) {
            this.container.style.display = "none";
            this.dispatchEvent("widget-hidden");
        }
        return this;
    }

    toggle() {
        if (this.container) {
            const isVisible = this.container.style.display !== "none";
            if (isVisible) {
                this.hide();
            } else {
                this.show();
            }
        }
        return this;
    }

    openPlayer() {
        if (this.widgetInstance && this.widgetInstance.openPlayer) {
            this.widgetInstance.openPlayer();
        }
        return this;
    }

    closePlayer() {
        if (this.widgetInstance && this.widgetInstance.closePlayer) {
            this.widgetInstance.closePlayer();
        }
        return this;
    }

    async refresh() {
        try {
            await this.loadStories();
            this.updateStories(this.stories);
            this.dispatchEvent("widget-refreshed");
        } catch (error) {
            console.error("Failed to refresh stories:", error);
            this.dispatchEvent("widget-error", { error: error.message });
        }
        return this;
    }

    destroy() {
        if (this.app) {
            this.app.unmount();
            this.app = null;
        }

        if (
            this.container &&
            this.container.parentNode &&
            !this.options.containerId
        ) {
            this.container.parentNode.removeChild(this.container);
        }

        this.widgetInstance = null;
        this.shadowRoot = null;
        this.analytics = null;
        this.dispatchEvent("widget-destroyed");
        return this;
    }

    updateStories(newStories) {
        this.stories = this.normalizeStories(newStories);
        if (this.app && this.app._instance) {
            this.app.unmount();
            this.mountWidget();
            this.dispatchEvent("stories-updated");
        }
        return this;
    }

    updateOptions(newOptions) {
        this.options = { ...this.options, ...newOptions };

        if (newOptions.position && this.container) {
            this.applyPositioning(this.container);
        }

        this.dispatchEvent("options-updated", { options: this.options });
        return this;
    }

    play() {
        if (this.widgetInstance && this.widgetInstance.togglePlayPause) {
            this.widgetInstance.togglePlayPause();
        }
        return this;
    }

    mute() {
        if (this.widgetInstance && this.widgetInstance.toggleMute) {
            this.widgetInstance.toggleMute();
        }
        return this;
    }

    setPlaybackRate(rate) {
        if (this.widgetInstance && this.widgetInstance.setVideoPlaybackRate) {
            this.widgetInstance.setVideoPlaybackRate(rate);
        }
        return this;
    }

    getStories() {
        return this.stories;
    }

    getCurrentStory() {
        if (this.widgetInstance && this.widgetInstance.currentStory) {
            return this.widgetInstance.currentStory;
        }
        return null;
    }

    isReady() {
        return this.isLoaded;
    }

    isPlaying() {
        if (this.widgetInstance && this.widgetInstance.player) {
            return this.widgetInstance.player.isPlaying;
        }
        return false;
    }

    getAnalyticsReport() {
        if (this.analytics) {
            return this.analytics.getSessionSummary();
        }
        return null;
    }

    setAnalyticsCustomDimension(key, value) {
        if (this.analytics) {
            this.analytics.setCustomDimension(key, value);
        }
        return this;
    }

    enableAnalytics() {
        if (this.analytics) {
            this.analytics.enable();
        }
        return this;
    }

    disableAnalytics() {
        if (this.analytics) {
            this.analytics.disable();
        }
        return this;
    }

    getVersion() {
        return "2.0.0";
    }

    getOptions() {
        return { ...this.options };
    }

    debug() {
        if (!this.options.devMode) {
            console.warn("Debug mode is not enabled");
            return null;
        }

        return {
            widget: this,
            app: this.app,
            instance: this.widgetInstance,
            container: this.container,
            shadowRoot: this.shadowRoot,
            stories: this.stories,
            options: this.options,
            analytics: this.analytics,
            isLoaded: this.isLoaded,
        };
    }
}

function autoInit() {
    const scripts = document.querySelectorAll(
        'script[src*="video-stories-widget"][data-api-key]',
    );

    scripts.forEach((script) => {
        const options = {
            apiKey: script.dataset.apiKey,
            containerId: script.dataset.containerId,
            autoplay: script.dataset.autoplay !== "false",
            muted: script.dataset.muted !== "false",
            theme: script.dataset.theme,
            position: script.dataset.position,
            size: script.dataset.size,
            showControls: script.dataset.showControls !== "false",
            devMode: script.dataset.devMode === "true",
        };

        // Analytics configuration from data attributes
        if (script.dataset.analyticsId) {
            options.analytics = {
                // Gets the GA4 ID from the client
                measurementId: script.dataset.analyticsId,

                // General analytics options
                options: {
                    enabled: script.dataset.analyticsEnabled !== "false",
                    debug: script.dataset.analyticsDebug === "true",
                },
            };
        }

        Object.keys(options).forEach((key) => {
            if (options[key] === undefined) delete options[key];
        });

        setTimeout(() => {
            const widget = new VideoStoriesWidget(options);
            script._widgetInstance = widget;
        }, 100);
    });
}

if (typeof window !== "undefined") {
    window.VideoStoriesWidget = VideoStoriesWidget;

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", autoInit);
    } else {
        autoInit();
    }
}

export default VideoStoriesWidget;
